import { QueryCache, QueryClient, type QueryKey } from "@tanstack/react-query";
import { toastError } from "@/shared/lib/toast";

declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: {
      /** Toasted when a fetch of this query fails (the API's message wins when it has one). */
      errorToast?: string;
      /** Only toast when nothing is on screen yet (a failed background refresh stays quiet). */
      errorToastWhenEmptyOnly?: boolean;
    };
  }
}

/**
 * Server-data cache for every portal (TanStack Query). One client per signed-in
 * portal session — see AppQueryProvider — so logging out drops everything.
 *
 * Freshness model: data is shown from cache instantly and revalidated in the
 * background. What keeps it current is, in order:
 *   1. socket events → invalidation (each portal's use…LiveQueries hook),
 *   2. refetch when the tab regains focus or the network comes back,
 *   3. per-query `staleTime` (how long a copy counts as fresh on remount).
 * No blanket interval polling — only screens with a server-side job in flight
 * (e.g. review processing) poll, via `refetchInterval`, and only while busy.
 */

/** How long data counts as fresh before a remount/focus refetches it. */
export const STALE = {
  /** Lists that change through other people's actions (queues, review items). */
  live: 10_000,
  /** Records, medications, uploads: change on sync/upload, which push events cover. */
  records: 30_000,
  /** Profile, settings — rarely change. */
  slow: 5 * 60_000,
} as const;

const isTest = import.meta.env.MODE === "test";

/**
 * Retry only a request that never reached the server (a network blip), up to
 * 3 times. Never a TIMEOUT: the server is still working on that request when
 * the browser gives up (aborting doesn't stop it), so retrying stacks the same
 * slow job up to 4 times and keeps the spinner up for minutes before any
 * error shows. A 4xx/5xx answer isn't retried either.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code;
  return failureCount < 3 && code === "NETWORK_ERROR";
}

export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    // One toast per failed fetch, however many screens share the query.
    queryCache: new QueryCache({
      onError: (error, query) => {
        const toast = query.meta?.errorToast;
        if (!toast) return;
        if (query.meta?.errorToastWhenEmptyOnly && query.state.data !== undefined) return;
        toastError((error as { message?: string } | null)?.message || toast);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: STALE.live,
        // Cached screens stay for the whole session unless unused for 30 min.
        gcTime: 30 * 60_000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        // Tests assert on the error state straight away — no backoff there.
        retry: isTest ? false : shouldRetryQuery,
      },
      mutations: { retry: false },
    },
  });
}

/**
 * Adapts the API layer's `{ data, error }` results to a query function:
 * resolves with the data, or throws the API error so the query (or mutation)
 * lands in its error state with that same error object.
 */
export async function unwrap<T, E>(request: Promise<{ data: T | null; error: E | null }>): Promise<T> {
  const { data, error } = await request;
  if (error !== null && error !== undefined) throw error;
  return data as T;
}

/** Invalidates several key prefixes at once (active ones refetch, others go stale). */
export function invalidateAll(client: QueryClient, keys: readonly QueryKey[]): Promise<void> {
  return Promise.all(keys.map((queryKey) => client.invalidateQueries({ queryKey }))).then(() => undefined);
}
